import React, { FC, useState, useEffect, useRef, useContext } from 'react';
import clsx from 'clsx';
import { Button, Container, Grid, makeStyles, Theme, Typography } from '@material-ui/core';

import useRouter from 'hooks/useRouter';
import ProfileContent from './components/ProfileContent';
import ContractContent from './components/ContractContent';
import JobContent from './components/JobContent';
import EquipmentContent from './components/EquipmentContent';
import JobNotesContent from './components/JobNotesContent';
import { InnerTabRequest, useIsTabActive, useTabs } from 'contexts/TabContext';
import { buildTabKey, buildTabLabel, TabRecordType } from 'constants/tabs';
import CustomizedTabs from 'components/CustomizedTabs';
import Breadcrumb from 'components/Breadcrumb';
import ActionSnackbar from 'components/ActionSnackbar';
import SideBarContent from 'components/SideBarContent';
import ClientDocumentForm from './components/ClientDocumentForm';

import AddIcon from '@material-ui/icons/Add';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DocumentIcon from '@material-ui/icons/Description';
import ErrorIcon from '@material-ui/icons/Error';
import DeleteIcon from '@material-ui/icons/Delete';
import MergeTypeIcon from '@material-ui/icons/MergeType';
import axios, { CancelTokenSource } from 'axios';
import { GET_CLIENT_BY_ID_URL, GET_DELETE_CLIENT_URL, GET_SERVICE_ADDRESS_BY_CLIENT_ID_URL } from 'constants/url';
import theme from 'theme';
import { dummyClientDetail } from 'constants/dummy';
import LoadingButtonIndicator from 'components/LoadingButtonIndicator';
import { StandardConfirmationDialog } from 'components/AppDialog';
import PasswordConfirmation from 'components/PasswordConfirmation';
import MergeClientDialog from 'components/MergeClientDialog';
import { CurrentUserContext } from 'contexts/CurrentUserContext';
import { getCurrentRoleGrants } from 'selectors';
import { hasAccessPermission } from 'utils';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  extendedIcon: {
    paddingRight: theme.spacing(1)
  },
  divider: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2)
  }
}));

interface Props {
  /** Supplied when the page is rendered inside a tab, where there is no route match to read. */
  recordId?: string;
  /** Section the link that opened this tab asked for; see InnerTabRequest. */
  innerTab?: InnerTabRequest;
}

const ClientDetailPage: FC<Props> = props => {
  const classes = useStyles();
  const { history, match } = useRouter();
  const params = props.recordId || match.params.id;

  /** Rendered inside a tab rather than straight off a route. */
  const isInsideTab = !!props.recordId;
  const isTabActive = useIsTabActive();
  const { closeTab, refreshPinnedTab, updateTabLabel } = useTabs();

  const [isRecordMissing, setRecordMissing] = useState<boolean>(false);
  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const childRef: any = useRef();

  const [tabs, setTabs] = useState<any[]>([
    { id: 0, name: 'Profile', module: 'CLIENTS' },
    { id: 1, name: 'Quotations', module: 'QUOTATIONS' },
    { id: 2, name: 'Jobs', module: 'JOBS' },
    { id: 3, name: 'Equipments' },
    { id: 4, name: 'Job Notes', count: 0 }
  ]);

  /**
   * The inner tab is component state, not router state.
   *
   * location.state is global to the app, so with two client tabs open both of them read the same
   * value — opening "Job Notes" on one client would quietly move the other one too. Inside a tab the
   * request therefore arrives as a prop; on a plain route it comes from ?tab=, which is what lets the
   * client list still deep link into a section when the tab bar is switched off.
   */
  const initialInnerTab = isInsideTab
    ? (props.innerTab && props.innerTab.index) || 0
    : Number(new URLSearchParams(history.location.search).get('tab')) || 0;
  const [selectedTab, setSelectedTab] = useState<number>(initialInnerTab);

  /**
   * The link was clicked on a client that is already open, so the tab was focused rather than
   * created and the initial state above never ran again. `at` changes on every request, including a
   * repeat of the same one, which is how a second "View Jobs" still lands.
   */
  const lastInnerTabRef = useRef<number>(props.innerTab ? props.innerTab.at : 0);
  useEffect(() => {
    if (!props.innerTab || props.innerTab.at === lastInnerTabRef.current) {
      return;
    }

    lastInnerTabRef.current = props.innerTab.at;
    setSelectedTab(props.innerTab.index);
  }, [props.innerTab]);
  const [clients, setClients] = useState<ClientDetailsModel>(dummyClientDetail);

  /**
   * Read from the record rather than kept alongside it.
   *
   * A rename writes through setClients, so a second copy of the name only ever went stale — the
   * breadcrumb kept showing the old one until the page was reloaded.
   */
  const clientName = clients.name;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [serviceAddressMaster, setServiceAddressMaster] = useState<Select[]>([]);

  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarVarient, setSnackbarVarient] = useState<'success' | 'error'>('success');
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarIsCountdown, setSnackbarIsCountdown] = useState<boolean>(false);

  const [openForm, setOpenForm] = useState<boolean>(false);
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState<boolean>(false);
  const [openMergeDialog, setOpenMergeDialog] = useState<boolean>(false);
  const [dataVersion, setDataVersion] = useState<number>(0);
  const [clientDocuments, setClientDocuments] = useState<JobDocumentModel[]>([]);
  const { currentUser } = useContext(CurrentUserContext);
  const currentRoleGrants = getCurrentRoleGrants(currentUser);
  const isDeleteActive = hasAccessPermission('CLIENTS', 'DELETE', currentRoleGrants);

  useEffect(() => {
    const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
    setIsLoading(true);

    const renderTabs = async () => {
      const newTabs = tabs
        .filter(({ module }) => !module || hasAccessPermission(module, 'ACCESS', currentRoleGrants))
        .map(({ id, name, count }) => ({ id, name, count }));

      // Set the filtered and formatted tabs
      setTabs(newTabs);
    };

    renderTabs();

    const getData = async () => {
      try {
        const { data } = await axios.get(`${GET_CLIENT_BY_ID_URL(params)}`, { cancelToken: cancelTokenSource.token });
        setClients(data.client);
        setClientDocuments(data.client.ClientDocuments);

        // Update tabs with the count
        setTabs(prevTabs => prevTabs.map(tab => (tab.id === 4 ? { ...tab, count: data.countJobNotes } : tab)));
      } catch (err) {
        console.log(err);
        const error = err as any;
        // A cancelled request rejects with the cancel object, which carries no data — reading
        // error.data blindly is what turned a closing tab into "error.data is undefined".
        const errorCode = error && error.data ? error.data.errorCode : undefined;

        if (errorCode === 10) {
          // Inside a tab this must stay local. Sending the whole app to /notfound would drag every
          // other open tab along with it, and a background tab could do it without being looked at.
          if (isInsideTab) {
            if (isMountedRef.current) {
              setRecordMissing(true);
            }
          } else {
            history.push({ pathname: `/notfound/` });
          }
        } else {
          setOpenSnackbar(true);
          setSnackbarVarient('error');
          setSnackbarMessage('Failed to load Client Details. Please try again later.');
        }

        setIsLoading(false);
      }
    };
    getData();

    const getServiceAddresses = async () => {
      try {
        if (Number(params) !== 0) {
          const { data } = await axios.get(`${GET_SERVICE_ADDRESS_BY_CLIENT_ID_URL(Number(params))}`, { cancelToken: cancelTokenSource.token });

          let serviceAddressData: Select[] = [];
          data.serviceAddresses.map((value: any) => {
            return serviceAddressData.push({ id: value.id, name: value.address });
          });

          setServiceAddressMaster(serviceAddressData);
        }
      } catch (err) {
        console.log(err);
      }
    };

    getServiceAddresses();

    setIsLoading(false);
    return () => {
      cancelTokenSource.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, selectedTab, dataVersion]);

  /**
   * TAB-06: the tab shows the client's name, not "Client #12".
   *
   * The link that opened the tab supplies the name when it has one, but it often does not — a
   * bookmark, a pasted URL, or a reload has only the id to go on. This is the point where the name
   * is actually known, and it runs again after a rename, so the tab never argues with the page.
   */
  useEffect(() => {
    if (!isInsideTab || !clientName) {
      return;
    }

    updateTabLabel(buildTabKey(TabRecordType.CLIENT, params), buildTabLabel(TabRecordType.CLIENT, params, clientName));
  }, [clientName, isInsideTab, params, updateTabLabel]);

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleSnackbar = (variant: 'success' | 'error', message: string, isCountdown?: boolean) => {
    /**
     * A countdown snackbar means "we are leaving this record" — today only the delete flow uses it,
     * and it works by pushing the whole app to the client list. Inside a tab that is the wrong move: the
     * list is already open behind this tab. Close the tab instead and reload the list, so it stops
     * showing a row that no longer exists.
     */
    if (isInsideTab && isCountdown && variant === 'success') {
      refreshPinnedTab();
      closeTab(buildTabKey(TabRecordType.CLIENT, params));
      return;
    }
    setOpenSnackbar(true);
    setSnackbarVarient(variant);
    setSnackbarMessage(message);
    if (isCountdown) {
      setSnackbarIsCountdown(isCountdown);
    }
  };

  const handleOpenCreateContract = () => {
    childRef && childRef.current!.handleOpenCreateContract();
  };

  const handleOpenCreateEquipment = () => {
    childRef && childRef.current!.handleOpenCreateEquipment();
  };

  const handleOpenClientDocument = () => {
    setOpenForm(true);
  };

  const handleConfirmDelete = () => {
    setOpenPasswordDialog(true);
    setIsDelete(false);
  };

  const handleCloseDelete = () => {
    setIsDelete(false);
    setOpenPasswordDialog(false);
  };

  const performActionAndRevertPage = (action: React.Dispatch<React.SetStateAction<any>>, actionParam: any) => {
    action(actionParam);
  };

  /**
   * A plain function, not a component. Declared as a component in here it was a new type on every
   * render, so React unmounted and remounted the open section each time this page re-rendered —
   * refetching its lists and dropping its filters. The key keeps the one remount that is wanted:
   * a different client starts the section from scratch.
   */
  const renderSelectedContent = (page: number) => {
    switch (page) {
      case 0:
        return <ProfileContent key={params} isLoading={isLoading} clients={clients} setClients={setClients} handleSnackbar={handleSnackbar} />;
      case 1:
        return (
          <ContractContent
            key={params}
            ref={childRef}
            clientId={params}
            serviceAddressMaster={serviceAddressMaster}
            currentRoleGrants={currentRoleGrants}
          />
        );
      case 2:
        return <JobContent key={params} clientId={params} serviceAddressMaster={serviceAddressMaster} />;
      case 3:
        return <EquipmentContent key={params} ref={childRef} clientId={params} serviceAddressMaster={serviceAddressMaster} />;
      case 4:
        return <JobNotesContent key={params} clientId={params} />;
      default:
        return <div />;
    }
  };

  /**
   * The record is gone — deleted by someone else, or by this user in another tab. Say so inside the
   * tab and let them close it, rather than moving the whole app somewhere they did not ask to go.
   */
  if (isRecordMissing) {
    return (
      <Container maxWidth={false} className={clsx(classes.root)}>
        <Typography variant='h6' gutterBottom>
          This client no longer exists
        </Typography>
        <Typography variant='body2' color='textSecondary' gutterBottom>
          It may have been deleted. Close this tab to carry on with the others.
        </Typography>
        <Button variant='contained' color='primary' disableElevation onClick={() => closeTab(buildTabKey(TabRecordType.CLIENT, params))}>
          Close Tab
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} className={clsx(classes.root)}>
      <Grid container spacing={3}>
        <Grid item sm={6}>
          <Typography variant='h4' gutterBottom>
            Clients Details
          </Typography>
          <Breadcrumb pages={['clients', clientName]} />
        </Grid>
      </Grid>
      <Grid container spacing={1} style={{ marginTop: theme.spacing(1), marginBottom: theme.spacing(5), maxHeight: 60 }}>
        <Grid item sm={6}>
          <CustomizedTabs
            // eslint-disable-next-line array-callback-return
            tabs={tabs}
            selectedTabId={selectedTab}
            onSelect={(tabId: number) => performActionAndRevertPage(setSelectedTab, tabId)}
          />
        </Grid>
        <Grid item container sm={6} justify='flex-end' alignItems='center'>
          <Grid item container sm={12} justify='flex-end'>
            {selectedTab === 0 ? (
              <Button
                variant='contained'
                disabled={isLoading}
                disableElevation
                style={{ marginRight: theme.spacing(2) }}
                startIcon={<MergeTypeIcon />}
                onClick={() => setOpenMergeDialog(true)}
              >
                Merge Client
                <LoadingButtonIndicator isLoading={isLoading} />
              </Button>
            ) : null}
            {isDeleteActive && selectedTab === 0 && (
              <Button
                variant='contained'
                disabled={isLoading}
                disableElevation
                style={{ background: theme.palette.error.main, color: '#ffffff', marginRight: theme.spacing(2) }}
                startIcon={<DeleteIcon />}
                onClick={() => setIsDelete(true)}
              >
                Delete Client
                <LoadingButtonIndicator isLoading={isLoading} />
              </Button>
            )}
            {selectedTab === 0 ? (
              <Button
                color='primary'
                size='medium'
                variant='contained'
                disableElevation
                startIcon={<DocumentIcon />}
                onClick={handleOpenClientDocument}
              >
                Client Documents
              </Button>
            ) : selectedTab === 1 ? (
              <Button color='primary' size='medium' variant='contained' disableElevation startIcon={<AddIcon />} onClick={handleOpenCreateContract}>
                New Quotation
              </Button>
            ) : (
              selectedTab === 3 && (
                <Button
                  color='primary'
                  size='medium'
                  variant='contained'
                  disableElevation
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreateEquipment}
                >
                  New Equipment
                </Button>
              )
            )}
          </Grid>
        </Grid>
      </Grid>
      {renderSelectedContent(selectedTab)}
      <SideBarContent title={'Client Documents'} open={isTabActive && openForm} onClickDrawer={() => setOpenForm(false)} width='50%'>
        <ClientDocumentForm
          clientId={Number(params)}
          clientDocuments={clientDocuments}
          setClientDocuments={setClientDocuments}
          handleClose={() => setOpenForm(false)}
          handleSnackbar={handleSnackbar}
        />
      </SideBarContent>
      <ActionSnackbar
        variant={snackbarVarient}
        message={snackbarMessage}
        open={isTabActive && openSnackbar}
        handleClose={handleCloseSnackbar}
        Icon={snackbarVarient === 'success' ? CheckCircleIcon : ErrorIcon}
        isCountdown={snackbarIsCountdown}
        redirectPath='clients'
      />
      {isTabActive && isDelete && (
        <StandardConfirmationDialog
          variant={'warning'}
          title='Confirm Client Deletion'
          message={`Please confirm that you want to permanently delete this client.\nThis action will also remove all associated quotations, jobs, and invoices.\nAre you sure you want to proceed?`}
          okLabel='OK'
          cancelLabel='cancel'
          open={isDelete}
          handleClose={handleCloseDelete}
          onConfirm={handleConfirmDelete}
        />
      )}
      {isTabActive && openMergeDialog && (
        <MergeClientDialog
          open={openMergeDialog}
          currentClientId={Number(params)}
          currentClientName={clientName}
          countdownOnDelete
          handleClose={() => setOpenMergeDialog(false)}
          handleSnackbar={handleSnackbar}
          fetchData={() => setDataVersion(version => version + 1)}
        />
      )}
      {isTabActive && openPasswordDialog && (
        <PasswordConfirmation
          open={openPasswordDialog}
          handleClose={handleCloseDelete}
          url={GET_DELETE_CLIENT_URL(Number(params))}
          title='Delete Client'
          message='delete client, redirecting to client list'
          handleSnackbar={handleSnackbar}
        />
      )}
    </Container>
  );
};

export default ClientDetailPage;
